import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Callout, Chip, Icon, tokens } from '@/design';
import {
  fetchPathProfile,
  pathProfileDistance,
  type PathProfileCoordinate,
  type PathProfileSample,
} from './pathProfile';
import { resolvePathDrawSurface } from './pathProfileSurface';

/**
 * The map host can attach a tick or two after the rail renders, so a missing
 * engine handle is retried before the tool reports the view as unsupported
 * (same reasoning as `PixelInspector`'s hover attach).
 */
const ATTACH_RETRY_MS = 120;
const ATTACH_MAX_ATTEMPTS = 20;

type ProfileState = 'idle' | 'drawing' | 'sampling' | 'ready' | 'error';

export interface PathProfilePanelProps {
  readonly container?: HTMLElement | null;
  readonly tilesUrl?: string | null;
  readonly enabled?: boolean;
  readonly fetcher?: typeof fetch;
}

function formatDistance(distance: number): string {
  return distance >= 1000
    ? `${(distance / 1000).toFixed(2)} km`
    : `${distance.toFixed(0)} m`;
}

function formatHeight(value: number): string {
  return `${value.toFixed(2)} m`;
}

function dedupeVertices(
  vertices: readonly PathProfileCoordinate[],
): PathProfileCoordinate[] {
  const result: PathProfileCoordinate[] = [];
  for (const vertex of vertices) {
    const previous = result[result.length - 1];
    if (!previous || pathProfileDistance(previous, vertex) > 0.05) {
      result.push(vertex);
    }
  }
  return result;
}

function ProfileChart({
  samples,
  activeIndex,
  onActiveIndexChange,
}: {
  readonly samples: readonly PathProfileSample[];
  readonly activeIndex: number | null;
  readonly onActiveIndexChange: (index: number | null) => void;
}) {
  const width = 520;
  const height = 210;
  const padding = { left: 54, right: 14, top: 18, bottom: 34 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const elevations = samples.map((sample) => sample.elevation);
  const rawMin = Math.min(...elevations);
  const rawMax = Math.max(...elevations);
  const spread = Math.max(rawMax - rawMin, 1);
  const min = rawMin - spread * 0.08;
  const max = rawMax + spread * 0.08;
  const total = samples[samples.length - 1]?.distance ?? 1;
  const x = (sample: PathProfileSample) =>
    padding.left + (sample.distance / total) * plotWidth;
  const y = (sample: PathProfileSample) =>
    padding.top + ((max - sample.elevation) / (max - min)) * plotHeight;
  const line = samples.map((sample) => `${x(sample)},${y(sample)}`).join(' ');
  const area = `${padding.left},${padding.top + plotHeight} ${line} ${padding.left + plotWidth},${padding.top + plotHeight}`;
  const active = activeIndex === null ? null : samples[activeIndex] ?? null;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      role="img"
      aria-label="Path height and depth profile"
      data-testid="path-profile-chart"
      onPointerMove={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const viewX = ((event.clientX - rect.left) / Math.max(rect.width, 1)) * width;
        const ratio = Math.max(0, Math.min(1, (viewX - padding.left) / plotWidth));
        onActiveIndexChange(Math.round(ratio * (samples.length - 1)));
      }}
      onPointerLeave={() => onActiveIndexChange(null)}
      style={{ display: 'block', touchAction: 'none' }}
    >
      <rect
        x={padding.left}
        y={padding.top}
        width={plotWidth}
        height={plotHeight}
        fill={tokens.color.surface[1]}
        stroke={tokens.color.surface[3]}
      />
      {[0, 0.5, 1].map((fraction) => (
        <line
          key={fraction}
          x1={padding.left}
          x2={padding.left + plotWidth}
          y1={padding.top + plotHeight * fraction}
          y2={padding.top + plotHeight * fraction}
          stroke={tokens.color.surface[3]}
          strokeDasharray={fraction === 0 || fraction === 1 ? undefined : '4 4'}
        />
      ))}
      <polygon points={area} fill={`${tokens.color.accent.primary}24`} />
      <polyline
        points={line}
        fill="none"
        stroke={tokens.color.accent.primary}
        strokeWidth={2.5}
        strokeLinejoin="round"
      />
      <text x={padding.left - 7} y={padding.top + 4} textAnchor="end" fill={tokens.color.ink.secondary} fontSize={11}>
        {rawMax.toFixed(1)}
      </text>
      <text x={padding.left - 7} y={padding.top + plotHeight} textAnchor="end" fill={tokens.color.ink.secondary} fontSize={11}>
        {rawMin.toFixed(1)}
      </text>
      <text x={padding.left} y={height - 8} fill={tokens.color.ink.secondary} fontSize={11}>0</text>
      <text x={padding.left + plotWidth} y={height - 8} textAnchor="end" fill={tokens.color.ink.secondary} fontSize={11}>
        {formatDistance(total)}
      </text>
      <text x={12} y={padding.top + plotHeight / 2} transform={`rotate(-90 12 ${padding.top + plotHeight / 2})`} textAnchor="middle" fill={tokens.color.ink.secondary} fontSize={11}>
        Height / depth (m)
      </text>
      {active ? (
        <>
          <line
            x1={x(active)}
            x2={x(active)}
            y1={padding.top}
            y2={padding.top + plotHeight}
            stroke={tokens.color.semantic.warning}
            strokeDasharray="3 3"
          />
          <circle cx={x(active)} cy={y(active)} r={5} fill={tokens.color.semantic.warning} />
        </>
      ) : null}
    </svg>
  );
}

export function PathProfilePanel({
  container,
  tilesUrl,
  enabled = true,
  fetcher,
}: PathProfilePanelProps) {
  const [state, setState] = useState<ProfileState>('idle');
  const [vertices, setVertices] = useState<PathProfileCoordinate[]>([]);
  const [samples, setSamples] = useState<PathProfileSample[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const verticesRef = useRef(vertices);
  const abortRef = useRef<AbortController | null>(null);
  verticesRef.current = vertices;

  const activeSample = activeIndex === null ? null : samples[activeIndex] ?? null;
  const stats = useMemo(() => {
    if (samples.length === 0) return null;
    const elevations = samples.map((sample) => sample.elevation);
    return {
      min: Math.min(...elevations),
      max: Math.max(...elevations),
      distance: samples[samples.length - 1].distance,
    };
  }, [samples]);

  const clear = useCallback(() => {
    abortRef.current?.abort();
    setState('idle');
    setVertices([]);
    setSamples([]);
    setActiveIndex(null);
    setError(null);
    resolvePathDrawSurface(container)?.clear();
  }, [container]);

  const samplePath = useCallback(async (
    nextVertices: readonly PathProfileCoordinate[],
  ) => {
    if (!tilesUrl) return;
    const clean = dedupeVertices(nextVertices);
    if (clean.length < 2) {
      setError('Click at least two different points before finishing.');
      return;
    }
    setVertices(clean);
    setState('sampling');
    setError(null);
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    try {
      const result = await fetchPathProfile(tilesUrl, clean, {
        sampleCount: 72,
        fetcher,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setSamples(result);
      setState('ready');
    } catch (nextError) {
      if (controller.signal.aborted) return;
      setError(nextError instanceof Error ? nextError.message : 'Profile sampling failed.');
      setState('error');
    }
  }, [fetcher, tilesUrl]);

  useEffect(() => {
    resolvePathDrawSurface(container)?.render(
      vertices,
      activeSample ? [activeSample.lon, activeSample.lat] : null,
    );
  }, [activeSample, container, vertices]);

  useEffect(() => () => {
    abortRef.current?.abort();
    resolvePathDrawSurface(container)?.clear();
  }, [container]);

  useEffect(() => {
    if (!enabled || state !== 'drawing') return;

    let detach: (() => void) | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    let cancelled = false;

    const handlers = {
      onPoint: (point: PathProfileCoordinate) => {
        setVertices((current) => dedupeVertices([...current, point]));
      },
      onFinish: (point: PathProfileCoordinate) => {
        void samplePath(dedupeVertices([...verticesRef.current, point]));
      },
    };

    const attach = () => {
      if (cancelled) return;
      detach = resolvePathDrawSurface(container)?.attach(handlers) ?? null;
      if (detach) return;
      attempts += 1;
      if (attempts >= ATTACH_MAX_ATTEMPTS) {
        setState('error');
        setError('Path drawing needs the 2D map view — switch to 2D map and try again.');
        return;
      }
      retryTimer = setTimeout(attach, ATTACH_RETRY_MS);
    };
    attach();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      detach?.();
    };
  }, [container, enabled, samplePath, state]);

  if (!tilesUrl) {
    return <Callout severity="warning">This layer has no raster source to sample.</Callout>;
  }

  return (
    <div
      data-testid="path-profile-panel"
      style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[2] }}
    >
      {state === 'idle' ? (
        <Callout severity="info" icon="timeline">
          Draw a path across the raster. Click to add points, then double-click
          the final point to calculate height and depth.
        </Callout>
      ) : null}
      {state === 'drawing' ? (
        <Chip
          icon="timeline"
          variant="info"
          label={`${vertices.length} point${vertices.length === 1 ? '' : 's'} · double-click to finish`}
        />
      ) : null}
      {state === 'sampling' ? (
        <Chip icon="pending" variant="info" label="Sampling path…" />
      ) : null}
      {error ? <Callout severity="error">{error}</Callout> : null}

      {samples.length > 0 && stats ? (
        <>
          <ProfileChart
            samples={samples}
            activeIndex={activeIndex}
            onActiveIndexChange={setActiveIndex}
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: tokens.space[1],
              fontFamily: tokens.font.family.mono,
              fontSize: tokens.font.role.engMono.size,
            }}
          >
            <span>Min {formatHeight(stats.min)}</span>
            <span>Max {formatHeight(stats.max)}</span>
            <span>{formatDistance(stats.distance)}</span>
          </div>
          <div
            aria-live="polite"
            data-testid="path-profile-live-value"
            style={{
              minHeight: tokens.density.rowDense,
              padding: tokens.space[2],
              border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
              borderRadius: tokens.radius.input,
              color: tokens.color.ink.primary,
              fontFamily: tokens.font.family.mono,
              fontSize: tokens.font.role.engMono.size,
            }}
          >
            {activeSample
              ? `${formatDistance(activeSample.distance)} · ${activeSample.elevation < 0 ? 'Depth' : 'Height'} ${formatHeight(Math.abs(activeSample.elevation))}`
              : 'Move the cursor over the graph to inspect a point.'}
          </div>
        </>
      ) : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: tokens.space[2] }}>
        <button
          type="button"
          onClick={() => {
            clear();
            setState('drawing');
          }}
          disabled={!enabled || state === 'drawing' || state === 'sampling'}
          data-testid="path-profile-draw"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[1],
            minHeight: tokens.density.touchMin,
            padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
            background: tokens.color.accent.primary,
            color: tokens.color.accent.on,
            border: 'none',
            borderRadius: tokens.radius.input,
            cursor: state === 'drawing' || state === 'sampling' ? 'default' : 'pointer',
          }}
        >
          <Icon name="timeline" size={20} decorative />
          {samples.length > 0 ? 'Draw new path' : 'Draw path'}
        </button>
        {(state !== 'idle' || vertices.length > 0) ? (
          <button
            type="button"
            onClick={clear}
            data-testid="path-profile-clear"
            style={{
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              background: tokens.color.surface[1],
              color: tokens.color.ink.primary,
              border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
              borderRadius: tokens.radius.input,
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        ) : null}
      </div>
    </div>
  );
}
